import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Block, Answer } from '../lib/scoring';
import questionsData from '../data/question.json';

const Test: React.FC = () => {
  const blocks: Block[] = questionsData.blocks as Block[];

  const [currentBlock, setCurrentBlock] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [selectedMost, setSelectedMost] = useState<string>('');
  const [selectedLeast, setSelectedLeast] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [autoAdvancing, setAutoAdvancing] = useState(false);
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const current = blocks[currentBlock];

  const cancelAutoAdvance = () => {
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
    }
    setAutoAdvancing(false);
  };

  const handleSelectMost = (word: string) => {
    if (selectedLeast === word) {
      setError("No puedes elegir la misma palabra como 'Más' y 'Menos'");
      cancelAutoAdvance();
      return;
    }
    cancelAutoAdvance();
    setSelectedMost(word);
    setError('');
  };

  const handleSelectLeast = (word: string) => {
    if (selectedMost === word) {
      setError("No puedes elegir la misma palabra como 'Más' y 'Menos'");
      cancelAutoAdvance();
      return;
    }
    cancelAutoAdvance();
    setSelectedLeast(word);
    setError('');
  };

  const handleNext = () => {
    cancelAutoAdvance();
    if (!selectedMost || !selectedLeast) {
      setError("Debes seleccionar una palabra 'Más' y una 'Menos'");
      return;
    }

    const newAnswer: Answer = {
      blockNumber: current.blockNumber,
      most: selectedMost,
      least: selectedLeast,
    };

    // Upsert: actualiza la respuesta si el bloque ya fue contestado antes
    // (evita doble conteo al retroceder y volver a avanzar)
    const updatedAnswers = answers.some(a => a.blockNumber === current.blockNumber)
      ? answers.map(a => a.blockNumber === current.blockNumber ? newAnswer : a)
      : [...answers, newAnswer];

    setAnswers(updatedAnswers);

    if (currentBlock < blocks.length - 1) {
      const nextBlock = blocks[currentBlock + 1];
      const nextAnswer = updatedAnswers.find(a => a.blockNumber === nextBlock.blockNumber);
      setCurrentBlock(currentBlock + 1);
      // Restaurar selecciones previas si el bloque siguiente ya fue respondido
      if (nextAnswer) {
        setSelectedMost(nextAnswer.most);
        setSelectedLeast(nextAnswer.least);
      } else {
        setSelectedMost('');
        setSelectedLeast('');
      }
      setError('');
    } else {
      handleFinishTest(updatedAnswers);
    }
  };

  // Auto-avance: se activa cuando ambas selecciones están completas
  useEffect(() => {
    if (selectedMost && selectedLeast) {
      setAutoAdvancing(true);
      autoAdvanceTimer.current = setTimeout(() => {
        handleNext();
      }, 700);
    }
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMost, selectedLeast]);

  const navigate = useNavigate();

  const handleFinishTest = (finalAnswers: Answer[]) => {
    localStorage.setItem('discAnswers', JSON.stringify(finalAnswers));
    navigate('/results');
  };

  const progress = Math.round((answers.length / blocks.length) * 100);

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-2xl mx-auto px-3 sm:px-4">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-10">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
            Test DISC - 28 Bloques
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 px-2">
            No hay respuestas correctas o incorrectas.
          </p>
        </div>

        {/* Barra de progreso */}
        <div className="mb-6 sm:mb-8">
          <div className="flex justify-between text-xs sm:text-sm text-gray-500 mb-2">
            <span>Bloque {current.blockNumber} de 28</span>
            <span className="font-semibold">{progress}%</span>
          </div>
          <div className="h-2.5 sm:h-3 bg-gray-200 rounded-full overflow-hidden shadow-sm">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Bloque actual */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
          <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 mb-6 sm:mb-8 text-center">
            Bloque {current.blockNumber}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {current.words.map((item, index) => (
              <div
                key={index}
                className="border-2 border-gray-200 rounded-xl p-3 sm:p-4 hover:border-blue-300 hover:bg-blue-50 transition-all"
              >
                <p className="text-base sm:text-lg font-medium text-center mb-3 sm:mb-4">
                  {item.word}
                </p>

                <div className="flex gap-2 sm:gap-3 justify-center">
                  <button
                    onClick={() => handleSelectMost(item.word)}
                    aria-pressed={selectedMost === item.word}
                    aria-label={`Seleccionar ${item.word} como más característico`}
                    className={`flex-1 min-h-[44px] px-3 sm:px-4 py-3 rounded-lg text-xs sm:text-sm font-medium transition-all active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-green-500 ${
                      selectedMost === item.word
                        ? 'bg-green-600 text-white shadow-md ring-2 ring-green-400'
                        : 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300'
                    }`}
                  >
                    MÁS
                  </button>

                  <button
                    onClick={() => handleSelectLeast(item.word)}
                    aria-pressed={selectedLeast === item.word}
                    aria-label={`Seleccionar ${item.word} como menos característico`}
                    className={`flex-1 min-h-[44px] px-3 sm:px-4 py-3 rounded-lg text-xs sm:text-sm font-medium transition-all active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500 ${
                      selectedLeast === item.word
                        ? 'bg-red-600 text-white shadow-md ring-2 ring-red-400'
                        : 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300'
                    }`}
                  >
                    MENOS
                  </button>
                </div>
              </div>
            ))}
          </div>

          {error && (
            <p className="text-red-600 text-center mt-4 sm:mt-6 font-medium text-sm sm:text-base px-2">{error}</p>
          )}
        </div>

        {/* Botones de navegación */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
          <button
            onClick={() => {
              cancelAutoAdvance();
              if (currentBlock > 0) {
                // Guardar respuesta actual con upsert antes de retroceder (sin duplicar)
                if (selectedMost && selectedLeast) {
                  const currentAnswer = { blockNumber: current.blockNumber, most: selectedMost, least: selectedLeast };
                  setAnswers(prev =>
                    prev.some(a => a.blockNumber === current.blockNumber)
                      ? prev.map(a => a.blockNumber === current.blockNumber ? currentAnswer : a)
                      : [...prev, currentAnswer]
                  );
                }
                const prevBlockNumber = blocks[currentBlock - 1].blockNumber;
                setCurrentBlock(currentBlock - 1);
                const prevAnswer = answers.find(a => a.blockNumber === prevBlockNumber);
                if (prevAnswer) {
                  setSelectedMost(prevAnswer.most);
                  setSelectedLeast(prevAnswer.least);
                } else {
                  setSelectedMost('');
                  setSelectedLeast('');
                }
                setError('');
              }
            }}
            disabled={currentBlock === 0}
            className="min-h-[44px] px-4 sm:px-6 py-3 text-gray-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 rounded-lg transition-colors text-sm sm:text-base flex-1 sm:flex-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
          >
            ← Anterior
          </button>

          <button
            onClick={handleNext}
            className={`relative overflow-hidden min-h-[44px] px-6 sm:px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg sm:rounded-xl transition-all active:scale-95 text-sm sm:text-base flex-1 sm:flex-none shadow-md hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 ${
              autoAdvancing ? 'ring-2 ring-blue-300' : ''
            }`}
          >
            {autoAdvancing && (
              <span
                className="absolute inset-0 bg-white/20 origin-left animate-[shrink_0.7s_linear_forwards]"
                style={{ animation: 'autobar 0.7s linear forwards' }}
              />
            )}
            <span className="relative z-10">
              {currentBlock === blocks.length - 1 ? 'Ver Resultados' : autoAdvancing ? 'Avanzando…' : 'Siguiente →'}
            </span>
          </button>
        </div>

        <p className="text-center text-xs text-gray-400 px-2">
          Se honesto. Esta información es para uso personal.
        </p>
      </div>
    </div>
  );
};

export default Test;
